import {defaultChannel} from '@/config';

export interface WebConfigValues {
  shareUrl: string | null;
  h5GamesUrl: string | null;
  gr8IframeUrl: string | null;
  scratchUrl: string | null;
  defaultChannel: string;
}

export type RemoteWebConfig = Partial<
  Record<keyof WebConfigValues, string | null>
>;

const DEFAULT_CHANNEL_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

const getTrimmedValue = (value?: string | null) => {
  const trimmedValue = typeof value === 'string' ? value.trim() : '';
  return trimmedValue || null;
};

const normalizeServiceUrl = (
  key: Exclude<keyof WebConfigValues, 'defaultChannel'>,
  value: string,
) => {
  const trimmedValue = value.trim();

  if (key === 'gr8IframeUrl') {
    return `${trimmedValue.replace(/\/+$/, '')}/`;
  }

  return key === 'shareUrl' ? trimmedValue : trimmedValue.replace(/\/+$/, '');
};

const isSecureRemoteUrl = (value: string) => {
  try {
    return new URL(value).protocol === 'https:';
  } catch (_) {
    return false;
  }
};

const isValidDefaultChannel = (value: string) =>
  DEFAULT_CHANNEL_PATTERN.test(value);

const getFallbackShareUrl = () => {
  const envShareUrl =
    getTrimmedValue(process.env.REACT_APP_SHARE_URL) ||
    getTrimmedValue(process.env.REACT_APP_SHARE_DOMAIN);
  if (envShareUrl) {
    return envShareUrl;
  }

  return typeof window === 'undefined' || !window.location?.origin
    ? null
    : window.location.origin;
};

export const createWebConfigFallback = (): WebConfigValues => ({
  shareUrl: getFallbackShareUrl(),
  h5GamesUrl:
    getTrimmedValue(process.env.REACT_APP_API_H5GAMES_URL) ||
    'https://h5-games.pages.dev',
  gr8IframeUrl: getTrimmedValue(process.env.REACT_APP_GR8_IFRAME_URL),
  scratchUrl: getTrimmedValue(process.env.REACT_APP_SCRATCH_URL),
  defaultChannel,
});

export const resolveBootstrapChannel = (
  explicitChannel?: string | null,
  storedChannel?: string | null,
  fallbackChannel = defaultChannel,
) =>
  getTrimmedValue(explicitChannel) ||
  getTrimmedValue(storedChannel) ||
  fallbackChannel;

const hasOwn = (value: object, key: PropertyKey) =>
  Object.prototype.hasOwnProperty.call(value, key);

export const mergeWebConfig = (
  fallback: WebConfigValues,
  remoteConfig: RemoteWebConfig,
): WebConfigValues => {
  const getUrlValue = (
    key: Exclude<keyof WebConfigValues, 'defaultChannel'>,
  ) => {
    if (!hasOwn(remoteConfig, key)) {
      return fallback[key];
    }

    const remoteValue = remoteConfig[key];
    if (remoteValue === null || typeof remoteValue !== 'string') {
      return fallback[key];
    }

    const trimmedRemoteValue = remoteValue.trim();
    if (!trimmedRemoteValue || !isSecureRemoteUrl(trimmedRemoteValue)) {
      return fallback[key];
    }

    return normalizeServiceUrl(key, trimmedRemoteValue);
  };

  const remoteDefaultChannel = getTrimmedValue(remoteConfig.defaultChannel);

  return {
    shareUrl: getUrlValue('shareUrl'),
    h5GamesUrl: getUrlValue('h5GamesUrl'),
    gr8IframeUrl: getUrlValue('gr8IframeUrl'),
    scratchUrl: getUrlValue('scratchUrl'),
    defaultChannel:
      remoteDefaultChannel && isValidDefaultChannel(remoteDefaultChannel)
        ? remoteDefaultChannel
        : fallback.defaultChannel,
  };
};

class RuntimeWebConfig {
  private readonly fallback = createWebConfigFallback();
  private current = this.fallback;

  get shareUrl() {
    return this.current.shareUrl;
  }

  get h5GamesUrl() {
    return this.current.h5GamesUrl;
  }

  get gr8IframeUrl() {
    return this.current.gr8IframeUrl;
  }

  get scratchUrl() {
    return this.current.scratchUrl;
  }

  get defaultChannel() {
    return this.current.defaultChannel;
  }

  applyRemote(remoteConfig: RemoteWebConfig) {
    this.current = mergeWebConfig(this.fallback, remoteConfig);
  }

  snapshot(): Readonly<WebConfigValues> {
    return {...this.current};
  }
}

export const webConfig = new RuntimeWebConfig();

export const buildInviteShareUrl = (
  configuredUrl: string | null,
  inviteCode: string,
  fallbackPath = '/invitation-apply',
) => {
  if (!configuredUrl) {
    return '';
  }

  try {
    const shareOrigin = new URL(configuredUrl).origin;
    const normalizedPath = fallbackPath.startsWith('/')
      ? fallbackPath
      : `/${fallbackPath}`;
    const shareUrl = new URL(normalizedPath, `${shareOrigin}/`);
    shareUrl.searchParams.set('code', inviteCode);
    return shareUrl.toString();
  } catch (_) {
    return '';
  }
};
